import { useEffect, useMemo, useState } from "react";
import { Heart, MapPin, Palmtree } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { slug } from "@/lib/geo";
import { focusOnMap } from "@/lib/mapFocus";
import { ISLANDS, OWNER, pretty } from "@/lib/islands";
import { DockSheet, SearchBox, plain } from "./DockSheet";

const Badge = ({ children, cls }) => <span className={`text-[10px] px-2 py-0.5 rounded-full border ${cls}`}>{children}</span>;

const HeartBtn = ({ on, onClick, label, testId }) => (
  <button onClick={onClick} aria-label={label} aria-pressed={on} data-testid={testId}
    className={`p-2 -mr-1 rounded-full transition-[transform,color] active:scale-90 ${on ? "text-amber-300" : "text-slate-500 hover:text-amber-300"}`}>
    <Heart size={17} fill={on ? "currentColor" : "none"} />
  </button>
);

// OpenStreetMap kinds worth listing here; mountains, lakes and parks are just noise
const SETTLEMENT = new Set(["city", "town", "village", "hamlet", "locality", "suburb", "island", "islet"]);

// Cities and islands matching the search, each tied to the country it's saved under
const usePlaces = (q, names) => {
  const [remote, setRemote] = useState([]);
  const t = plain(q.trim());
  useEffect(() => {
    if (t.length < 2) return setRemote([]);
    const id = setTimeout(() => api.searchCities("", q.trim()).then(setRemote, () => setRemote([])), 250);
    return () => clearTimeout(id);
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps
  return useMemo(() => {
    if (t.length < 2) return [];
    const islands = ISLANDS.filter((i) => plain(i.name).includes(t)).sort((a, b) => plain(b.name).startsWith(t) - plain(a.name).startsWith(t));
    const countries = new Set(names.map(plain));
    const key = (p) => `${plain(p.name)}|${p.country}`;
    const seen = new Set(islands.map(key));
    const others = (countries.has(t) ? [] : remote)
      .filter((r) => !countries.has(plain(r.name)) && (!r.kind || SETTLEMENT.has(r.kind)))
      .map((r) => ({ name: r.name, x: r.x, y: r.y, kind: r.kind || "city", country: OWNER[r.country] || r.country, region: OWNER[r.country] ? r.country : r.region || "" }))
      .filter((p) => !seen.has(key(p)) && seen.add(key(p)));
    return [...islands, ...others].slice(0, 10);
  }, [t, remote, names]);
};

const PlaceRow = ({ p, wanted, onOpen, onWish }) => (
  <li className="flex items-center gap-2 border-b border-white/5" data-testid={`countries-place-${slug(p.name)}`}>
    <button onClick={onOpen} className="flex-1 min-w-0 flex items-center gap-2.5 py-2.5 text-left text-sm hover:text-emerald-200 transition-colors" data-testid={`countries-place-open-${slug(p.name)}`}>
      {p.kind === "island" ? <Palmtree size={14} className="text-teal-300 shrink-0" /> : <MapPin size={14} className="text-slate-500 shrink-0" />}
      <span className="truncate">{p.name} <span className="text-slate-500">· in {p.region ? `${pretty(p.region)}, ` : ""}{pretty(p.country)}</span></span>
    </button>
    <HeartBtn on={wanted} onClick={onWish} label={`Add ${p.name} to bucketlist`} testId={`countries-place-wish-${slug(p.name)}`} />
  </li>
);

export const CountriesSheet = ({ open, onOpenChange, data, b, names }) => {
  const [q, setQ] = useState("");
  const been = useMemo(() => new Set(data.visited.map((v) => v.country)), [data.visited]);
  const want = useMemo(() => new Set(data.wishlist.map((w) => w.country)), [data.wishlist]);
  const wantPlace = useMemo(() => new Set(data.wishlist.flatMap((w) => (w.cities || []).map((c) => `${c}|${w.country}`))), [data.wishlist]);
  const list = useMemo(() => {
    const t = plain(q.trim());
    return names.filter((n) => !t || plain(n).includes(t) || plain(pretty(n)).includes(t));
  }, [names, q]);
  const places = usePlaces(q, names);
  const pick = (n) => { onOpenChange(false); setTimeout(() => focusOnMap(n), 250); };
  const wishCountry = (n) => (want.has(n) ? toast(`${n} is already on your bucketlist`) : b.addWish(n));
  let letter = "";
  return (
    <DockSheet open={open} onOpenChange={onOpenChange} title="Countries" description={`${names.length} countries A–Z · type a city or island to find its country`} testId="countries-sheet">
      <SearchBox value={q} onChange={setQ} placeholder="Search countries, cities or islands…" testId="countries-search-input" />
      <ul className="mt-3 flex-1 overflow-y-auto pr-1" data-testid="countries-list">
        {list.map((n) => {
          const first = plain(n[0]).toUpperCase();
          const head = first !== letter && (letter = first);
          return (
            <li key={n}>
              {head && <p className="sticky top-0 z-10 bg-[#0E131D] font-mono text-[10px] text-slate-500 pt-3 pb-1">{first}</p>}
              <div className="flex items-center gap-2 border-b border-white/5">
                <button onClick={() => pick(n)} className="flex-1 min-w-0 flex items-center justify-between gap-3 py-2.5 text-left text-sm hover:text-emerald-200 transition-colors" data-testid={`countries-item-${slug(n)}`}>
                  <span className="truncate">{n}</span>
                  <span className="flex gap-1.5 shrink-0">
                    {been.has(n) && <Badge cls="border-emerald-400/40 text-emerald-300">Been there</Badge>}
                    {want.has(n) && <Badge cls="border-amber-400/40 text-amber-300">Want to go</Badge>}
                  </span>
                </button>
                <HeartBtn on={want.has(n)} onClick={() => wishCountry(n)} label={`Add ${n} to bucketlist`} testId={`countries-wish-${slug(n)}`} />
              </div>
            </li>
          );
        })}
        {places.length > 0 && (
          <li data-testid="countries-places">
            <p className="sticky top-0 z-10 bg-[#0E131D] font-mono text-[10px] text-slate-500 pt-4 pb-1">CITIES & ISLANDS</p>
            <ul>{places.map((p) => <PlaceRow key={`${p.name}|${p.country}|${p.x}`} p={p} wanted={wantPlace.has(`${p.name}|${p.country}`)} onOpen={() => pick(p.country)} onWish={() => b.wishPlace(p.country, p)} />)}</ul>
          </li>
        )}
        {!list.length && !places.length && <li className="py-8 text-center text-sm text-slate-500" data-testid="countries-empty">No country, city or island matches that.</li>}
      </ul>
    </DockSheet>
  );
};
